import { acceptedStates, baseUrl, ParcelType, FilterType } from '../constants'

import {
  APIRequestPayload,
  APIResponse,
  ErrorCode,
  HttpException,
  IWallPayload,
  ScrapeResult,
  XHR_CONTENT_TYPE,
  XHR_REQUEST_TYPE,
} from '@/common/interfaces'
import { DialogMessageType, Note, Wall, Sharing, IUser, IURL, IWall, INote, Subject } from '@/common/classes'

import useDialogStore from '@/composables/dialog'

interface ServiceInterface {
  base: {
    isConnected: () => boolean
    getEncodedParams: (query: Record<string, string>) => string
    handleError: (status: number, error: unknown, callback?: () => void) => void
    apiRequest: <T>(
      {
        errorCode,
        route,
        method,
        query, // A dictionary containing values to convert to a URL query string (e.g. ?a=yyy&b=zzz)
        body, // Can be FormData, JSON, text, or Object (unknown type). Use contentType to specify which
        headers,
        credentials,
        contentType, // Chosen using XHR_CONTENT_TYPE enumerator. Defaults to 'application/json'
        baseURL,
        requiresFeedback,
      }: APIRequestPayload,
      callback?: () => void,
    ) => Promise<T>
  }
  request: {
    anonymousLogin: (payload: IWallPayload, callback?: () => void) => Promise<APIResponse<{ id: string }>>
    sessionIsActive: () => Promise<APIResponse<{ authenticated: boolean }>>
    samlLogin: () => Promise<APIResponse<Response>>
    logout: (callback?: () => void) => Promise<APIResponse<IURL>>
    fetchUserDetails: (callback?: () => void) => Promise<APIResponse<IUser>>
    fetchUserWalls: (mode: string, callback?: () => void) => Promise<APIResponse<IWall[]>>
    fetchTemplates: (callback?: () => void) => Promise<APIResponse<IWall[]>>
    scrape: (url: string, callback?: () => void) => Promise<APIResponse<ScrapeResult>>

    getWallByKey: (key: 'id' | 'pin', value: string, callback?: () => void) => Promise<APIResponse<IWall>>

    togglePermaLinkControl: (id: string) => Promise<APIResponse<IWall>>
    getWall: (id: string, callback?: () => void) => Promise<APIResponse<IWall>>
    wallExport: (wallId: string, fileName: string, filterType: FilterType, languageCode: string, callback?: () => void) => void

    subscribeToWall: (wallId: string, callback?: () => void) => Promise<APIResponse<IWall>>
    createShare: (wallId: string, body: string | Sharing) => Promise<APIResponse<IWall>>
    changeShare: (wallId: string, shareId: string, body: string | Sharing, parcelType: ParcelType) => Promise<APIResponse<IWall>>
    deleteShare: (wallId: string, share_id: string) => Promise<APIResponse<IWall>>
    createWall: (body: string | Wall, callback?: () => void) => Promise<APIResponse<IWall>>
    updateWall: (wallId: string, body: string | Wall, callback?: () => void) => Promise<APIResponse<IWall>>
    deleteWall: (wallId: string, callback?: () => void) => Promise<APIResponse<boolean>>
    createNote: (wallId: string, body: Note, callback?: () => void) => Promise<APIResponse<IWall>>
    fetchNotes: (wallId: string, callback?: () => void) => Promise<APIResponse<INote[]>>
    fetchSubjects: (callback?: () => void) => Promise<APIResponse<Subject[]>>
    updateNote: (wallId: string, noteId: string, parcelType: ParcelType, body: string | Note, callback?: () => void) => Promise<APIResponse<INote>>
    createPin: (body: Wall, callback?: () => void) => Promise<APIResponse<IWall>>
    deletePin: (body: Wall, callback?: () => void) => Promise<APIResponse<IWall>>
  }
}

let connected = false

function useApiService(): ServiceInterface {
  const dialogStore = useDialogStore()
  const apiBaseURL = '/api'
  const authBaseURL = '/auth'

  const getSuccessTag = () => {
    switch (navigator.language) {
      // @ts-ignore
      case 'nb-NO' || 'nn-NO' || 'no':
        return 'Vellykket!'
      default:
        return 'Success!'
    }
  }

  const base = {
    isConnected: (): boolean => {
      return connected
    },
    getEncodedParams: (query: Record<string, string>): string => {
      // Encode query and body
      let qs = ''
      const queryKeys = query ? Reflect.ownKeys(query).map((k) => k.toString()) : []
      if (query && queryKeys.length > 0) {
        qs += '?'
        queryKeys.forEach((key, index) => {
          qs += `${key}=${query[key]}`
          qs += index < queryKeys.length - 1 ? '&' : ''
        })
      }
      return encodeURI(qs)
    },

    /**
     * handleError()
     * handles errors that might occur during or after the request
     * @param status
     * @param error
     */
    handleError: (status: number, error: unknown, callback?: () => void): void => {
      const err = error as HttpException
      console.error(err)
      dialogStore.actions.pushMessage(status.toString(), DialogMessageType.Error, `An error occured during sending the request ${err.message}`)
      if (callback !== undefined) callback
    },

    /**
     * apiRequest
     * prepares a xhrhttprequest according to the given parameters
     * @param param0
     * @returns
     */
    apiRequest: async <T>(
      {
        errorCode,
        route,
        method,
        query, // A dictionary containing values to convert to a URL query string (e.g. ?a=yyy&b=zzz)
        body = {}, // Can be FormData, JSON, text, or Object (unknown type). Use contentType to specify which
        headers,
        credentials = true,
        contentType, // Chosen using XHR_CONTENT_TYPE enumerator. Defaults to 'application/json'
        baseURL,
        requiresFeedback = false,
      }: APIRequestPayload,
      callback?: () => void,
    ): Promise<T> => {
      // Set token if available
      const ct = contentType || XHR_CONTENT_TYPE.JSON
      const _baseUrl = baseURL !== undefined ? baseURL : baseUrl

      // Set headers
      headers = {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': ct,
        ...headers,
      }
      const ltiHandoff = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ltiHandoff') : null
      if (ltiHandoff && !headers.Authorization) {
        headers.Authorization = `Bearer ${ltiHandoff}`
      }

      let data: XMLHttpRequestBodyInit
      if (ct !== XHR_CONTENT_TYPE.MULTIPART) {
        // Convert body to correct format based on contentType
        if (typeof body === 'string' && ct === XHR_CONTENT_TYPE.URLENCODED) {
          data = body
        } else {
          data = JSON.stringify(body)
        }
      } else {
        data = body as FormData
        delete headers['Content-Type'] // Adding an explicit content type causes problems with Multer. Allow the browser to set it.
      }

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        // Event listener must be added before calling open()
        xhr.addEventListener('loadend', () => {
          let response = xhr.response
          if (!acceptedStates.includes(xhr.status)) {
            let exception
            connected = false
            if (response) {
              try {
                exception = new HttpException(xhr.status, `${response.message || response.name}`)
              } catch (e) {
                if (e) {
                  console.error(e)
                  exception = new HttpException(xhr.status, `Fatal: ${e}`)
                }
              }
              response = {
                status: xhr.status,
                error: exception,
                dataType: XHR_CONTENT_TYPE.JSON,
                data: null,
              } as APIResponse<T>
              if (!route.includes('webscraper')) {
                dialogStore.actions.pushMessage(`Errorcode: ${errorCode}`, DialogMessageType.Error, '', 15000)
                if (exception) console.error(exception.message)
              }
            }
          } else {
            // 403 is a special case, the server intends the user to log out and return to the homepage
            if (xhr.status == 403 && !window.location.pathname.startsWith('/lti')) {
              connected = false
              request.logout()
            }
            connected = true
            response = {
              status: xhr.status,
              dataType: XHR_CONTENT_TYPE.JSON,
              data: response,
            } as APIResponse<T>
            if (requiresFeedback && xhr.status.toString()[0] !== '4')
              // only display success if no 4XX response was given
              dialogStore.actions.pushMessage(xhr.status.toString(), DialogMessageType.Success, getSuccessTag())
            resolve(response)
          }
          reject('no response was given')
        })

        let encodedQueries = ''
        if (query !== undefined) {
          // query is an optional parameter
          encodedQueries = base.getEncodedParams(query)
        }

        const url = `${_baseUrl}${route}${encodedQueries}`

        xhr.open(method, url)
        xhr.responseType = 'json'
        xhr.withCredentials = credentials

        if (headers !== undefined) {
          // headers is an optional argument
          for (const key of Object.keys(headers)) {
            xhr.setRequestHeader(key, headers[key])
          }
        }

        try {
          if (method === XHR_REQUEST_TYPE.GET) {
            xhr.send()
          } else {
            xhr.send(data)
          }
        } catch (error: unknown) {
          console.error(error)
          base.handleError(xhr.status, error, callback)
          reject({
            status: xhr.status,
            error: new HttpException(xhr.status, `${xhr.response.message || xhr.response.name}`),
            dataType: XHR_CONTENT_TYPE.JSON,
            data: null,
          } as APIResponse<T>)
        }
      })
    },
  }

  /* USER FUNCTIONS */

  const request = {
    samlLogin: async (callback?: () => void): Promise<APIResponse<Response>> => {
      return base.apiRequest<APIResponse<Response>>(
        {
          errorCode: ErrorCode.LOGIN,
          route: `/saml`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.TEXT,
          baseURL: baseUrl + authBaseURL,
        },
        callback,
      )
    },

    /**
     * This function is taking name, permalink and PIN for anonymous users, establishes a session (if details are correct)
     * and returns the desired wall
     * @param payload
     * @param callback
     * @returns
     */
    anonymousLogin: async (payload: IWallPayload): Promise<APIResponse<{ id: string }>> => {
      return base.apiRequest<APIResponse<{ id: string }>>({
        errorCode: ErrorCode.LOGIN,
        route: `/anonymous`,
        method: XHR_REQUEST_TYPE.POST,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + authBaseURL,
        body: payload,
      })
    },

    // Check the existance of a session (isAuthenticated = true for FEIDE etc, false for anonymous)
    sessionIsActive: async (): Promise<APIResponse<{ authenticated: boolean }>> => {
      return base.apiRequest<APIResponse<{ authenticated: boolean }>>({
        errorCode: ErrorCode.LOGIN,
        route: `/sessionActive`,
        method: XHR_REQUEST_TYPE.GET,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + authBaseURL,
      })
    },

    logout: async (callback?: () => void): Promise<APIResponse<IURL>> => {
      connected = false
      const response = await base.apiRequest<APIResponse<IURL>>(
        {
          errorCode: ErrorCode.LOGIN,
          route: `/logout`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + authBaseURL,
        },
        callback,
      )
      window.location.href = '/'
      return response
    },

    fetchUserDetails: async (callback?: () => void): Promise<APIResponse<IUser>> => {
      return base.apiRequest<APIResponse<IUser>>(
        {
          errorCode: ErrorCode.USER,
          route: `/user`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    fetchUserWalls: async (mode: string, callback?: () => void): Promise<APIResponse<IWall[]>> => {
      return base.apiRequest<APIResponse<IWall[]>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/user/walls/`,
          method: XHR_REQUEST_TYPE.GET,
          query: { mode: mode },
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    fetchTemplates: async (callback?: () => void): Promise<APIResponse<IWall[]>> => {
      return base.apiRequest<APIResponse<IWall[]>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/user/templates`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    /* SCRAPE */
    scrape: async (url: string, callback?: () => void): Promise<APIResponse<ScrapeResult>> => {
      return base.apiRequest<APIResponse<ScrapeResult>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/webscraper`,
          method: XHR_REQUEST_TYPE.POST,
          body: {
            url: url,
          },
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    /* WALL FUNCTIONS */

    togglePermaLinkControl: async (id: string): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>({
        errorCode: ErrorCode.WALL,
        route: `/wall/${id}/permalink`,
        method: XHR_REQUEST_TYPE.PUT,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + apiBaseURL,
        requiresFeedback: true,
      })
    },

    getWall: async (id: string, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/${id}`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    getWallByKey: async (key: 'id' | 'pin', value: string, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/by/${key}/${value}`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    subscribeToWall: async (wallId: string, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.MQTT,
          route: `/wall/${wallId}/subscribe`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    wallExport: (wallId: string, fileName: string, filterType: FilterType, languageCode: string, callback?: () => void) => {
      const link = document.createElement('a')
      link.href = `${baseUrl + apiBaseURL}/wall/${wallId}/export?fileName=${fileName}&filterType=${filterType}&languageCode=${languageCode}`
      link.click()
      link.href = ''
    },

    createShare: async (wallId: string, body: string | Sharing): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>({
        errorCode: ErrorCode.SHARING,
        route: `/wall/${wallId}/share`,
        method: XHR_REQUEST_TYPE.POST,
        body,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + apiBaseURL,
        requiresFeedback: true,
      })
    },

    changeShare: async (wallId: string, shareId: string, body: string | Sharing, parcelType: ParcelType): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>({
        errorCode: ErrorCode.SHARING,
        route: `/wall/${wallId}/share/${shareId}/${parcelType}`,
        method: XHR_REQUEST_TYPE.PUT,
        body,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + apiBaseURL,
        requiresFeedback: true,
      })
    },

    deleteShare: async (wallId: string, share_id: string): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>({
        errorCode: ErrorCode.SHARING,
        route: `/wall/${wallId}/share/${share_id}`,
        method: XHR_REQUEST_TYPE.DELETE,
        credentials: true,
        contentType: XHR_CONTENT_TYPE.JSON,
        baseURL: baseUrl + apiBaseURL,
        requiresFeedback: true,
      })
    },

    createWall: async (body: string | Wall, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall`,
          method: XHR_REQUEST_TYPE.POST,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    updateWall: async (wallId: string, body: string | Wall, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/${wallId}`,
          method: XHR_REQUEST_TYPE.PUT,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    createPin: async (body: Wall, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/${body._id}/pin`,
          method: XHR_REQUEST_TYPE.PUT,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    deletePin: async (body: Wall, callback?: () => void): Promise<APIResponse<IWall>> => {
      return base.apiRequest<APIResponse<IWall>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/${body._id}/pin`,
          method: XHR_REQUEST_TYPE.DELETE,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    deleteWall: async (wallId: string, callback?: () => void): Promise<APIResponse<boolean>> => {
      return base.apiRequest<APIResponse<boolean>>(
        {
          errorCode: ErrorCode.WALL,
          route: `/wall/${wallId}`,
          method: XHR_REQUEST_TYPE.DELETE,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
          requiresFeedback: true,
        },
        callback,
      )
    },

    /* NOTE FUNCTIONS */

    createNote: async (wallId: string, body: Note, callback?: () => void): Promise<APIResponse<INote>> => {
      return await base.apiRequest<APIResponse<INote>>(
        {
          errorCode: ErrorCode.NOTE,
          route: `/wall/${wallId}/note`,
          method: XHR_REQUEST_TYPE.POST,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    fetchNotes: async (wallId: string, callback?: () => void): Promise<APIResponse<INote[]>> => {
      return await base.apiRequest<APIResponse<INote[]>>(
        {
          errorCode: ErrorCode.NOTE,
          route: `/wall/${wallId}/note`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    fetchSubjects: async (callback?: () => void): Promise<APIResponse<Subject[]>> => {
      return await base.apiRequest<APIResponse<Subject[]>>(
        {
          errorCode: ErrorCode.NOTE,
          route: `/user/subjects`,
          method: XHR_REQUEST_TYPE.GET,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },

    updateNote: async (
      wallId: string,
      noteId: string,
      parcelType: ParcelType,
      body: string | Note,
      callback?: () => void,
    ): Promise<APIResponse<INote>> => {
      return await base.apiRequest<APIResponse<INote>>(
        {
          errorCode: ErrorCode.NOTE,
          route: `/wall/${wallId}/note/${noteId}/${parcelType}`,
          method: XHR_REQUEST_TYPE.PUT,
          body,
          credentials: true,
          contentType: XHR_CONTENT_TYPE.JSON,
          baseURL: baseUrl + apiBaseURL,
        },
        callback,
      )
    },
  }

  return { base, request }
}

export default useApiService
